import React, { useState } from 'react';
import {
  globalVariableSchema,
  IGlobalVariable,
} from '../../Interfaces/IGlobalVariable';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { GlobalVariableEdit } from '../../GlobalVariable/GlobalVariableEdit';
import { useAlert } from '../../context/AlertContext';
import {
  useDeleteGlobalVariable,
  useWriteGlobalVariable,
} from '../../hooks/reactQuerys/GlobalVariableQueries';
import { SaveButton } from '../../generic/SaveButton';

interface IProps {
  globalVariables: IGlobalVariable[];
  refetechGlobalVariables: () => void;
}

export const GlobalVariableOverview: React.FC<IProps> = ({
  globalVariables,
  refetechGlobalVariables,
}) => {
  const { showAlert } = useAlert();

  const { writeGlobalVariable, isPendingWriteGlobalVariable } =
    useWriteGlobalVariable();
  const { deleteGlobalVariable, isPendingDeleteGlobalVariable } =
    useDeleteGlobalVariable();
  const isPending =
    isPendingWriteGlobalVariable || isPendingDeleteGlobalVariable;

  const [editGlobalVariable, setEditGlobalVariable] =
    useState<IGlobalVariable | null>(null);
  const [removeGlobalVariable, setRemoveGlobalVariable] =
    useState<IGlobalVariable | null>(null);

  const handleNew = () => {
    setEditGlobalVariable(globalVariableSchema.parse({}));
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!editGlobalVariable) {
      return null;
    }

    try {
      await writeGlobalVariable(editGlobalVariable);
      showAlert({
        text: 'Erfolgreich gespeichert!',
        severity: 'success',
      });
      refetechGlobalVariables();
      setEditGlobalVariable(null);
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Es ist ein Fehler aufgetretten!',
        severity: 'error',
      });
    }
  };

  const handleRemove = async () => {
    if (!removeGlobalVariable) {
      return null;
    }

    try {
      await deleteGlobalVariable(removeGlobalVariable);
      showAlert({
        text: 'Erfolgreich gelöscht!',
        severity: 'success',
      });
      refetechGlobalVariables();
      setRemoveGlobalVariable(null);
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Es ist ein Fehler aufgetretten!',
        severity: 'error',
      });
    }
  };

  return (
    <>
      {editGlobalVariable && (
        <Dialog
          open
          onClose={() => setEditGlobalVariable(null)}
          maxWidth='sm'
          fullWidth
        >
          <form onSubmit={handleSave}>
            <DialogContent>
              <GlobalVariableEdit
                editGlobalVariable={editGlobalVariable}
                setEditGlobalVariable={setEditGlobalVariable}
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant='outlined'
                onClick={() => setEditGlobalVariable(null)}
              >
                Abbruch
              </Button>
              <SaveButton isPending={isPending} type='submit' />
            </DialogActions>
          </form>
        </Dialog>
      )}

      {removeGlobalVariable && (
        <Dialog
          open
          onClose={() => setRemoveGlobalVariable(null)}
          maxWidth='sm'
          fullWidth
        >
          <DialogContent>
            <Typography>
              Sind Sie sicher, dass Sie die Varialbe{' '}
              <i>{removeGlobalVariable.GlobalVariable}</i> entfültig löschen
              wollen?
            </Typography>
          </DialogContent>
          <DialogActions>
            <SaveButton
              isPending={isPending}
              variant='outlined'
              label='Löschen'
              onClick={handleRemove}
            />
            <Button
              variant='contained'
              onClick={() => setEditGlobalVariable(null)}
            >
              Abbruch
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Typography variant='h4'>
        Globale Variablen
        <IconButton sx={{ float: 'right' }} onClick={handleNew}>
          <Add />
        </IconButton>
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={50}>Nr.</TableCell>
            <TableCell width={250}>Variable</TableCell>
            <TableCell>Wert</TableCell>
            <TableCell width={90}>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {globalVariables.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <i>Keine Variablen gesetzt!</i>
              </TableCell>
            </TableRow>
          )}
          {globalVariables.map(gv => (
            <TableRow key={`row-globalvariable-${gv.idGlobalVariable}`}>
              <TableCell>{gv.idGlobalVariable}</TableCell>
              <TableCell>{gv.GlobalVariable}</TableCell>
              <TableCell>{gv.VariableValue}</TableCell>
              <TableCell>
                <IconButton onClick={() => setEditGlobalVariable(gv)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setRemoveGlobalVariable(gv)}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};
